import { Card, Col, Row, Typography } from 'antd';
import React from 'react';

import type { HealthStatus, PortfolioStats, PortfolioStatusPreset } from '@/api/portfolio/api';
import { HealthDot } from '@/components/common/HealthIndicator';

const { Text } = Typography;

interface StatCardDef {
  preset: PortfolioStatusPreset;
  label: string;
  caption: string;
  /** Status modifier used for the accent + pill styling. */
  tone: 'success' | 'warning' | 'error' | 'info';
  pill: string;
  /** RYG color whose dot renders in front of the pill label; omitted for Missing Update. */
  dot?: Exclude<HealthStatus, 'pending'>;
}

const STAT_CARDS: StatCardDef[] = [
  {
    preset: 'on_track',
    label: 'On Track',
    caption: 'All indicators green',
    tone: 'success',
    pill: 'Green',
    dot: 'green',
  },
  {
    preset: 'watching',
    label: 'Watching',
    caption: 'At least one indicator yellow',
    tone: 'warning',
    pill: 'Yellow',
    dot: 'yellow',
  },
  {
    preset: 'at_risk',
    label: 'At Risk',
    caption: 'At least one indicator red',
    tone: 'error',
    pill: 'Red',
    dot: 'red',
  },
  {
    preset: 'missing_update',
    label: 'Missing Update',
    caption: 'Last update older than 7 days',
    tone: 'info',
    pill: '⚠',
  },
];

export interface PortfolioStatCardsProps {
  stats: PortfolioStats;
  activePreset: PortfolioStatusPreset | null;
  onSelect: (preset: PortfolioStatusPreset) => void;
}

/** Four clickable health summary cards; clicking applies the matching table filter. */
const PortfolioStatCardsComponent: React.FC<PortfolioStatCardsProps> = ({
  stats,
  activePreset,
  onSelect,
}) => (
  <Row gutter={[16, 16]} className="pf-stat-cards">
    {STAT_CARDS.map((card) => {
      const isActive = activePreset === card.preset;
      return (
        <Col key={card.preset} xs={24} sm={12} xl={6}>
          <Card
            hoverable
            role="button"
            aria-pressed={isActive}
            tabIndex={0}
            className={`pf-stat-card pf-stat-card--${card.tone}${
              isActive ? ` pf-stat-card--active pf-stat-card--active-${card.tone}` : ''
            }`}
            onClick={() => onSelect(card.preset)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelect(card.preset);
              }
            }}
          >
            <div className="pf-stat-card__head">
              <Text className="pf-stat-card__label">{card.label}</Text>
              <span className={`pf-stat-pill pf-stat-pill--${card.tone}`}>
                {card.dot ? <HealthDot status={card.dot} /> : null}
                {card.pill}
              </span>
            </div>
            <div className="pf-stat-card__value">{stats[card.preset]}</div>
            <div className="pf-stat-card__caption-row">
              <Text type="secondary" className="pf-stat-card__caption">
                {card.caption}
              </Text>
              {isActive ? <span className="pf-stat-card__filtering">✓ filtering</span> : null}
            </div>
          </Card>
        </Col>
      );
    })}
  </Row>
);

/** Memoized so filter/search/table state changes don't re-render the stat cards. */
export const PortfolioStatCards = React.memo(PortfolioStatCardsComponent);

export default PortfolioStatCards;
